// frontend/src/components/ClinicMap/ClinicMapWidget.tsx
import React, { useState, useRef } from 'react';
import { useTheme } from '../../hooks/useTheme';
import { useRooms, RoomDoctor, getImageUrl } from '../../services/api';
import { useLanguage } from '../../hooks/useLanguage';
import { useQueueWidget } from '../../hooks/useQueueWidget';

interface TooltipState {
  visible: boolean;
  x: number;
  y: number;
  roomNumber: string;
  doctors: RoomDoctor[];
  roomName: string;
}

interface PinnedState {
  roomNumber: string;
  doctors: RoomDoctor[];
  roomName: string;
  x: number;
  y: number;
}

const ClinicMapWidget: React.FC = () => {
  const { theme } = useTheme();
  const { t } = useLanguage();
  const queueWidget = useQueueWidget();
  const [activeFloor, setActiveFloor] = useState('1');
  const [tooltip, setTooltip] = useState<TooltipState>({
    visible: false, x: 0, y: 0, roomNumber: '', doctors: [], roomName: '',
  });
  const [pinned, setPinned] = useState<PinnedState | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const { data: rooms = [] } = useRooms(activeFloor);

  // Группируем по типу (только с заполненными координатами)
  const withCoords = rooms.filter(
    r => r.svg_x != null && r.svg_y != null && r.svg_width != null && r.svg_height != null
  );
  const buildingRooms = withCoords.filter(r => r.room_type === 'building');
  const hallways      = withCoords.filter(r => r.room_type === 'hallway');
  const serviceZones  = withCoords.filter(r => r.room_type === 'service');
  const cabinets      = withCoords.filter(r => r.room_type === 'room');

  // Словарь: номер → врачи
  const roomDoctors = React.useMemo(() => {
    const map: Record<string, RoomDoctor[]> = {};
    rooms.forEach(r => { map[r.number] = r.doctors; });
    return map;
  }, [rooms]);

  // Динамический viewBox по реальным координатам элементов
  const viewBox = React.useMemo(() => {
    if (withCoords.length === 0) return '0 0 900 700';
    const PAD = 12;
    const minX = Math.min(...withCoords.map(r => r.svg_x!)) - PAD;
    const minY = Math.min(...withCoords.map(r => r.svg_y!)) - PAD;
    const maxX = Math.max(...withCoords.map(r => r.svg_x! + r.svg_width!)) + PAD;
    const maxY = Math.max(...withCoords.map(r => r.svg_y! + r.svg_height!)) + PAD;
    return `${minX} ${minY} ${maxX - minX} ${maxY - minY}`;
  }, [withCoords]);

  // Границы viewBox для clamp tooltip-а
  const [vbMinX, vbMinY, vbW] = viewBox.split(' ').map(Number);

  const isDark = theme === 'dark';

  function getSVGCoords(e: React.MouseEvent): { x: number; y: number } {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const svgP = pt.matrixTransform(svg.getScreenCTM()!.inverse());
    return { x: svgP.x, y: svgP.y };
  }

  function handleRoomEnter(e: React.MouseEvent, number: string) {
    const coords = getSVGCoords(e);
    setTooltip({
      visible: true,
      x: coords.x,
      y: coords.y,
      roomNumber: number,
      doctors: roomDoctors[number] ?? [],
      roomName: `${t('home.map.room')} ${number}`,
    });
  }

  function handleRoomMove(e: React.MouseEvent) {
    if (!tooltip.visible) return;
    const coords = getSVGCoords(e);
    setTooltip(prev => ({ ...prev, x: coords.x, y: coords.y }));
  }

  function handleRoomLeave() {
    setTooltip(prev => ({ ...prev, visible: false }));
  }

  function handleRoomClick(e: React.MouseEvent, number: string) {
    e.stopPropagation();
    const coords = getSVGCoords(e);
    const doctors = roomDoctors[number] ?? [];
    const roomName = `${t('home.map.room')} ${number}`;
    setPinned(prev =>
      prev?.roomNumber === number ? null : { roomNumber: number, doctors, roomName, x: coords.x, y: coords.y }
    );
  }

  function handleSvgClick() {
    setPinned(null);
  }

  // Иконки отделений для tooltip: уникальные по врачу (первое отделение с иконкой)
  function getDeptIcon(d: RoomDoctor): string {
    const dept = d.departments.find(dep => dep.icon);
    return dept ? getImageUrl(dept.icon) : '';
  }

  // Tooltip position (stay inside viewBox 900×700)
  const TT_W = 230;
  const TT_H = tooltip.doctors.length > 0 ? 32 + tooltip.doctors.length * 44 : 56;
  const ttX = Math.min(tooltip.x + 14, vbMinX + vbW - TT_W - 8);
  const ttY = Math.max(tooltip.y - TT_H - 10, vbMinY + 8);

  // Цвета
  const roomFill        = isDark ? '#192e4d' : '#c2dbff';
  const roomFillHover   = '#0c63e9';
  const roomStroke      = isDark ? '#2d4a6e' : '#4a6fa5';
  const buildingFill    = isDark ? '#1f1f1f' : '#f8f9ff';
  const buildingStroke  = isDark ? 'rgba(30,50,64,0.75)' : '#2d3748';
  const hallwayFill     = isDark ? '#252525' : '#edeef2';
  const hallwayStroke   = isDark ? '#333' : '#a0aec0';
  const svcFill         = isDark ? '#263040' : '#e2e8f0';
  const svcStroke       = isDark ? '#3a4f6a' : '#718096';
  const labelFill       = isDark ? '#a8c5e8' : '#1a3a6b';
  const svcLabelFill    = isDark ? '#7a9fc0' : '#4a5568';

  // Рендер проёма двери: перекрываем часть стены фоном (20px по центру)
  function renderDoor(
    x: number, y: number, w: number, h: number,
    door: '' | 'N' | 'S' | 'W' | 'E',
    bgColor: string,
    strokeW: number,
  ) {
    if (!door) return null;
    const DW = Math.min(20, door === 'N' || door === 'S' ? w - 4 : h - 4);
    const SW = strokeW + 0.5; // чуть шире обводки, чтобы надёжно перекрыть
    if (door === 'N') return <rect x={x + w / 2 - DW / 2} y={y - SW / 2}       width={DW} height={SW} fill={bgColor} />;
    if (door === 'S') return <rect x={x + w / 2 - DW / 2} y={y + h - SW / 2}   width={DW} height={SW} fill={bgColor} />;
    if (door === 'W') return <rect x={x - SW / 2}           y={y + h / 2 - DW / 2} width={SW} height={DW} fill={bgColor} />;
    if (door === 'E') return <rect x={x + w - SW / 2}       y={y + h / 2 - DW / 2} width={SW} height={DW} fill={bgColor} />;
    return null;
  }

  const hasMap = cabinets.length > 0 || serviceZones.length > 0 || buildingRooms.length > 0;

  return (
    <section className="w-full pt-8 pb-4">
      {/* Заголовок и переключатель */}
      <div className="max-w-[1312px] mx-auto px-4 sm:px-8">
        <h2 className="text-[#0c63e9] dark:text-[#599bff] text-5xl font-bold leading-[56px] mb-2">
          {t('home.map.title')}
        </h2>
        <p className="text-[#454545] dark:text-[#e4e4e4] text-xl mt-4 leading-6 mb-4">
          {t('home.map.subtitle')}
        </p>

        <div className="flex gap-2 mb-4">
          {(['1', '2', '3', 'basement'] as const).map(key => (
            <button
              key={key}
              onClick={() => setActiveFloor(key)}
              className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
                activeFloor === key
                  ? 'bg-[#0c63e9] text-white'
                  : 'bg-[#edeef2] dark:bg-[#192e4d] text-[#454545] dark:text-[#e4e4e4] hover:bg-[#c2dbff] dark:hover:bg-[#1e3a5f]'
              }`}
            >
              {t(`home.map.floor_${key}`)}
            </button>
          ))}
        </div>
      </div>

      {/* SVG карта */}
      <div
        className="border-y border-[#d9e5f1] dark:border-[rgba(30,50,64,0.75)] overflow-x-auto min-h-[300px]"
        style={{ background: isDark ? '#1f1f1f' : '#fdfdfd' }}
      >
        {hasMap ? (
          <svg
            ref={svgRef}
            viewBox={viewBox}
            style={{ display: 'block', width: '100%', minWidth: 480, height: 'auto' }}
            onMouseMove={handleRoomMove}
            onClick={handleSvgClick}
          >
            {/* Контур здания */}
            {buildingRooms.map(r => (
              <rect key={r.number}
                x={r.svg_x!} y={r.svg_y!} width={r.svg_width!} height={r.svg_height!} rx="4"
                fill={buildingFill} stroke={buildingStroke} strokeWidth="2"
              />
            ))}

            {/* Холлы/коридоры */}
            {hallways.map(r => (
              <rect key={r.number}
                x={r.svg_x!} y={r.svg_y!} width={r.svg_width!} height={r.svg_height!} rx="1"
                fill={hallwayFill} stroke={hallwayStroke} strokeWidth="0.5"
              />
            ))}

            {/* Служебные зоны */}
            {serviceZones.map(r => {
              const isQueue = Boolean(r.opens_queue_widget) && queueWidget.available;
              return (
              <g
                key={r.number}
                {...(isQueue ? {
                  role: 'button',
                  tabIndex: 0,
                  'aria-label': t('queueWidget.title'),
                  style: { cursor: 'pointer' },
                  onClick: (e: React.MouseEvent) => { e.stopPropagation(); queueWidget.open(); },
                  onKeyDown: (e: React.KeyboardEvent) => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); queueWidget.open(); }
                  },
                } : {})}
              >
                <rect
                  x={r.svg_x!} y={r.svg_y!} width={r.svg_width!} height={r.svg_height!} rx="2"
                  fill={isQueue ? (isDark ? '#192e4d' : '#c2dbff') : svcFill}
                  stroke={isQueue ? (isDark ? '#599bff' : '#0c63e9') : svcStroke}
                  strokeWidth={isQueue ? 1.5 : 1} strokeDasharray={isQueue ? undefined : '4 2'}
                />
                {renderDoor(r.svg_x!, r.svg_y!, r.svg_width!, r.svg_height!, r.door, isDark ? '#1f1f1f' : '#fdfdfd', 1)}
                {r.svg_icon ? (() => {
                  const PAD = 4;
                  const iconSz = Math.min(Math.min(r.svg_width!, r.svg_height!) - PAD * 2, 50);
                  return (
                    <image
                      href={getImageUrl(r.svg_icon)}
                      x={r.svg_x! + (r.svg_width! - iconSz) / 2}
                      y={r.svg_y! + (r.svg_height! - iconSz) / 2}
                      width={iconSz} height={iconSz}
                      opacity={0.9}
                      style={{ pointerEvents: 'none' }}
                    />
                  );
                })() : (
                  <text
                    x={r.svg_x! + r.svg_width! / 2} y={r.svg_y! + r.svg_height! / 2}
                    textAnchor="middle" dominantBaseline="middle"
                    fontSize="9" fill={svcLabelFill} style={{ pointerEvents: 'none' }}
                  >{r.name}</text>
                )}
              </g>
              );
            })}

            {/* Кабинеты */}
            {cabinets.map(room => {
              const x = room.svg_x!;
              const y = room.svg_y!;
              const w = room.svg_width!;
              const h = room.svg_height!;
              const doctors = roomDoctors[room.number] ?? [];
              const hasDoctors = doctors.length > 0;
              const isHovered = tooltip.visible && tooltip.roomNumber === room.number;
              const isPinned = pinned?.roomNumber === room.number;
              const isActive = isHovered || isPinned;

              // Уникальные иконки отделений (по одной на отделение)
              const ICON_SIZE = 20;
              const ICON_GAP = 4;
              const INNER_W = w - 8; // отступы по 4px с каждой стороны
              const showIcons = hasDoctors;
              const deptIcons: { id: number; url: string }[] = [];
              if (showIcons) {
                const seen = new Set<number>();
                for (const d of doctors) {
                  for (const dept of d.departments) {
                    if (!seen.has(dept.id) && dept.icon) {
                      seen.add(dept.id);
                      deptIcons.push({ id: dept.id, url: getImageUrl(dept.icon) });
                    }
                  }
                }
              }

              // Раскладка: номер + иконки в ряд, перенос по ширине
              // При 3+ иконках — только первая иконка + счётчик «+N»
              const NUM_W = room.number.length * 6 + 4;
              const overflow = deptIcons.length > 2;
              const visibleIcons = overflow ? deptIcons.slice(0, 1) : deptIcons;
              const overflowCount = overflow ? deptIcons.length - 1 : 0;
              const BADGE_W = overflowCount > 0 ? (overflowCount > 8 ? 16 : 13) : 0;
              // ширина блока иконок: видимые иконки + зазоры + бейдж
              const iconBlockW = visibleIcons.length * ICON_SIZE
                + Math.max(0, visibleIcons.length - 1) * ICON_GAP
                + (BADGE_W > 0 ? ICON_GAP + BADGE_W : 0);

              type LayoutItem = { kind: 'num' } | { kind: 'icons' };
              const items: LayoutItem[] = deptIcons.length > 0
                ? [{ kind: 'num' }, { kind: 'icons' }]
                : [{ kind: 'num' }];

              // Разбиваем на строки по INNER_W
              const lines: LayoutItem[][] = [];
              let currentLine: LayoutItem[] = [];
              let currentW = 0;
              for (const item of items) {
                const itemW = item.kind === 'num' ? NUM_W : iconBlockW;
                const needed = currentLine.length > 0 ? ICON_GAP + itemW : itemW;
                if (currentLine.length > 0 && currentW + needed > INNER_W) {
                  lines.push(currentLine);
                  currentLine = [item];
                  currentW = itemW;
                } else {
                  currentLine.push(item);
                  currentW += needed;
                }
              }
              if (currentLine.length > 0) lines.push(currentLine);

              const LINE_H = Math.max(12, ICON_SIZE);
              const totalContentH = lines.length * LINE_H + (lines.length - 1) * 3;
              const contentStartY = y + (h - totalContentH) / 2;

              const textFill = isActive ? '#ffffff' : (hasDoctors && !isDark ? '#ffffff' : labelFill);
              return (
                <g key={room.number} style={{ cursor: 'pointer' }}
                  onMouseEnter={e => handleRoomEnter(e, room.number)}
                  onMouseLeave={handleRoomLeave}
                  onClick={e => handleRoomClick(e, room.number)}
                >
                  <rect
                    x={x} y={y} width={w} height={h} rx="2"
                    fill={isActive ? roomFillHover : (hasDoctors ? (isDark ? '#1a3a6b' : '#4a8fd4') : roomFill)}
                    stroke={isPinned ? '#ff9900' : (isActive ? '#004dc3' : roomStroke)}
                    strokeWidth={isActive ? 1.5 : 1}
                    style={{ transition: 'fill 0.15s ease' }}
                  />
                  {renderDoor(x, y, w, h, room.door, isDark ? '#1f1f1f' : '#fdfdfd', isActive ? 1.5 : 1)}
                  {lines.map((line, li) => {
                    const itemW = (item: LayoutItem) => item.kind === 'num' ? NUM_W : iconBlockW;
                    const lineW = line.reduce((acc, item, ii) => acc + (ii > 0 ? ICON_GAP : 0) + itemW(item), 0);
                    const lineX = x + (w - lineW) / 2;
                    const lineY = contentStartY + li * (LINE_H + 3);
                    let curX = lineX;
                    return (
                      <g key={li} style={{ pointerEvents: 'none' }}>
                        {line.map((item, ii) => {
                          const offsetX = curX;
                          curX += itemW(item) + (ii < line.length - 1 ? ICON_GAP : 0);
                          if (item.kind === 'num') {
                            if (room.svg_icon) {
                              const PAD = 2;
                              const iconSz = Math.min(Math.min(NUM_W, LINE_H) - PAD * 2, 50);
                              return (
                                <image key="num"
                                  href={getImageUrl(room.svg_icon)}
                                  x={offsetX + (NUM_W - iconSz) / 2}
                                  y={lineY + (LINE_H - iconSz) / 2}
                                  width={iconSz} height={iconSz}
                                  opacity={0.9}
                                />
                              );
                            }
                            return (
                              <text key="num"
                                x={offsetX + NUM_W / 2} y={lineY + LINE_H / 2}
                                textAnchor="middle" dominantBaseline="middle"
                                fontSize="10" fontWeight="600" fill={textFill}
                                style={{ transition: 'fill 0.15s ease' }}
                              >{room.number}</text>
                            );
                          }
                          // блок иконок: видимые + бейдж «+N»
                          const iconY = lineY + (LINE_H - ICON_SIZE) / 2;
                          const badgeX = offsetX + visibleIcons.length * (ICON_SIZE + ICON_GAP);
                          const badgeCY = lineY + LINE_H / 2;
                          return (
                            <g key="icons">
                              {visibleIcons.map((icon, i) => (
                                <image key={icon.id}
                                  href={icon.url}
                                  x={offsetX + i * (ICON_SIZE + ICON_GAP)}
                                  y={iconY}
                                  width={ICON_SIZE} height={ICON_SIZE}
                                  opacity={0.9}
                                />
                              ))}
                              {overflowCount > 0 && (
                                <g>
                                  <rect x={badgeX} y={badgeCY - 6} width={BADGE_W} height={12} rx="3"
                                    fill="rgba(255,255,255,0.25)" />
                                  <text x={badgeX + BADGE_W / 2} y={badgeCY}
                                    textAnchor="middle" dominantBaseline="middle"
                                    fontSize="8" fontWeight="700" fill="#ffffff">
                                    +{overflowCount}
                                  </text>
                                </g>
                              )}
                            </g>
                          );
                        })}
                      </g>
                    );
                  })}
                </g>
              );
            })}

            {/* Hover tooltip (SVG, не кликабельный) */}
            {tooltip.visible && !pinned && (
              <g style={{ pointerEvents: 'none' }}>
                <rect
                  x={ttX} y={ttY} width={TT_W} height={TT_H} rx="8"
                  fill={isDark ? '#1e2a3a' : '#1a202c'} opacity="0.95"
                />
                <text
                  x={ttX + 12} y={ttY + 18}
                  fontSize="13" fontWeight="700" fill="#ffffff" dominantBaseline="middle"
                >{tooltip.roomName}</text>
                {tooltip.doctors.length === 0 ? (
                  <text x={ttX + 12} y={ttY + 38} fontSize="11" fill="#94b8e8" dominantBaseline="middle">
                    {t('home.map.noDoctors')}
                  </text>
                ) : (
                  tooltip.doctors.map((d, i) => {
                    const iconUrl = getDeptIcon(d);
                    return (
                      <g key={d.id}>
                        {iconUrl && (
                          <image href={iconUrl} x={ttX + 12} y={ttY + 24 + i * 44} width="16" height="16" />
                        )}
                        <text x={ttX + (iconUrl ? 32 : 12)} y={ttY + 36 + i * 44} fontSize="11" fontWeight="600" fill="#e2e8f0" dominantBaseline="middle">
                          {d.full_name}
                        </text>
                        <text x={ttX + 12} y={ttY + 52 + i * 44} fontSize="10" fill="#94b8e8" dominantBaseline="middle">
                          {d.specialization}
                        </text>
                      </g>
                    );
                  })
                )}
              </g>
            )}

            {/* Pinned popup (foreignObject — кликабельные ссылки на врачей) */}
            {pinned && (() => {
              const PT_W = 220;
              const PT_ROW_H = 48;
              const PT_H = pinned.doctors.length > 0 ? 40 + pinned.doctors.length * PT_ROW_H : 60;
              const ptX = Math.min(pinned.x + 14, vbMinX + vbW - PT_W - 8);
              const ptY = Math.max(pinned.y - PT_H - 10, vbMinY + 8);
              return (
                <g>
                  {/* фон */}
                  <rect
                    x={ptX} y={ptY} width={PT_W} height={PT_H} rx="8"
                    fill={isDark ? '#1e2a3a' : '#1a202c'} opacity="0.97"
                    style={{ pointerEvents: 'none' }}
                  />
                  {/* оранжевый бордер */}
                  <rect
                    x={ptX} y={ptY} width={PT_W} height={PT_H} rx="8"
                    fill="none" stroke="#ff9900" strokeWidth="1.5"
                    style={{ pointerEvents: 'none' }}
                  />
                  <foreignObject x={ptX} y={ptY} width={PT_W} height={PT_H}>
                    <div
                      style={{
                        fontFamily: 'inherit',
                        padding: '10px 12px 8px',
                        boxSizing: 'border-box',
                        width: PT_W,
                        height: PT_H,
                      }}
                      onClick={e => e.stopPropagation()}
                    >
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#ffffff', marginBottom: 6 }}>
                        {pinned.roomName}
                      </div>
                      {pinned.doctors.length === 0 ? (
                        <div style={{ fontSize: 11, color: '#94b8e8' }}>{t('home.map.noDoctors')}</div>
                      ) : (
                        pinned.doctors.map((d, i) => {
                          const iconUrl = getDeptIcon(d);
                          return (
                            <a
                              key={d.id}
                              href={`/doctors/${d.slug}`}
                              style={{
                                display: 'block',
                                marginBottom: 6,
                                textDecoration: 'none',
                                borderRadius: 4,
                                padding: `2px 4px 2px ${iconUrl ? 26 : 4}px`,
                                transition: 'background 0.15s',
                              }}
                              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.1)'; }}
                              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                            >
                              <div style={{ fontSize: 11, fontWeight: 600, color: '#e2e8f0' }}>{d.full_name}</div>
                              <div style={{ fontSize: 10, color: '#94b8e8' }}>{d.specialization}</div>
                            </a>
                          );
                        })
                      )}
                    </div>
                  </foreignObject>
                  {/* Иконки отделений — SVG <image> поверх foreignObject */}
                  {pinned.doctors.map((d, i) => {
                    const iconUrl = getDeptIcon(d);
                    if (!iconUrl) return null;
                    return (
                      <image
                        key={d.id}
                        href={iconUrl}
                        x={ptX + 16}
                        y={ptY + 36 + i * PT_ROW_H + (PT_ROW_H / 2 - 8)}
                        width="16"
                        height="16"
                        style={{ pointerEvents: 'none' }}
                      />
                    );
                  })}
                </g>
              );
            })()}
          </svg>
        ) : (
          <div className="flex flex-col items-center justify-center py-20 text-[#a0aec0] dark:text-[#4a5568]">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M3 9.5L12 3l9 6.5V20a1 1 0 01-1 1H4a1 1 0 01-1-1V9.5z"/>
              <path d="M9 21V12h6v9"/>
            </svg>
            <p className="mt-3 text-sm">{t('home.map.comingSoon')}</p>
          </div>
        )}
      </div>

      {/* Легенда */}
      <div className="max-w-[1312px] mx-auto px-4 sm:px-8">
        <div className="flex flex-wrap gap-4 mt-4 text-xs text-[#454545] dark:text-[#a0aec0]">
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 rounded-sm bg-[#4a8fd4] dark:bg-[#1a3a6b] border border-[#4a6fa5]" />
            {t('home.map.legendHasDoctors')}
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 rounded-sm bg-[#c2dbff] dark:bg-[#192e4d] border border-[#4a6fa5]" />
            {t('home.map.legendNoDoctors')}
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 rounded-sm bg-[#e2e8f0] dark:bg-[#263040]" style={{ border: '1px dashed #718096' }} />
            {t('home.map.legendService')}
          </span>
        </div>
      </div>
      {queueWidget.modal}
    </section>
  );
};

export default ClinicMapWidget;
